import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Search, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FQCPage = ({ user }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/trc/devices?status=repair_completed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDevices(response.data.devices);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const loadTemplate = async (osType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/iqc/templates/${osType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTemplate(response.data.template);
      }
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const handleSelectDevice = async (device) => {
    setSelectedDevice(device);
    setResponses({});
    // Load template based on device OS (default to Android)
    await loadTemplate('Android');
  };

  const handleResponseChange = (categoryId, parameterId, optionId) => {
    setResponses({
      ...responses,
      [`${categoryId}_${parameterId}`]: optionId
    });
  };

  const calculateGrade = () => {
    if (!template) return null;

    let gradeScores = {
      excellent: 0,
      superb: 0,
      good: 0,
      fair: 0,
      okay: 0
    };

    Object.entries(responses).forEach(([key, optionId]) => {
      // Find the option in template
      template.categories.forEach(category => {
        category.parameters.forEach(parameter => {
          const option = parameter.options.find(o => o.option_id === optionId);
          if (option && option.selling_grade) {
            Object.keys(gradeScores).forEach(grade => {
              if (option.selling_grade[grade]) {
                gradeScores[grade]++;
              }
            });
          }
        });
      });
    });

    // Determine final grade
    if (gradeScores.excellent === Object.keys(responses).length) return 'excellent';
    if (gradeScores.excellent > 0 && gradeScores.superb === 0) return 'superb';
    if (gradeScores.good > gradeScores.fair + gradeScores.okay) return 'good';
    if (gradeScores.fair > gradeScores.okay) return 'fair';
    return 'okay';
  };

  const handleSubmitFQC = async (result) => {
    if (Object.keys(responses).length === 0) {
      alert('Please complete the FQC form');
      return;
    }

    const grade = calculateGrade();
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fqcEngineerId = user?.employee_id || 'FQC001';
      
      const response = await axios.post(
        `${API_URL}/api/trc/fqc/submit`,
        {
          device_id: selectedDevice.trc_device_id,
          fqc_engineer_id: fqcEngineerId,
          iqc_template_id: template.template_id,
          responses: responses,
          result: result,
          grade: result === 'pass' ? grade : null,
          fail_reason: result === 'fail' ? 'Device did not meet quality standards' : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`FQC ${result.toUpperCase()}! ${result === 'pass' ? `Grade: ${grade}` : ''}`);
        setSelectedDevice(null);
        setResponses({});
        loadDevices();
      }
    } catch (error) {
      console.error('Error submitting FQC:', error);
      alert('Error submitting FQC');
    } finally {
      setLoading(false);
    }
  };

  const filteredDevices = devices.filter(d =>
    d.did.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (selectedDevice && template) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Final Quality Check (FQC)</h1>
                <p className="text-sm text-gray-600">Device: {selectedDevice.did} - {selectedDevice.brand} {selectedDevice.model}</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedDevice(null)}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Back to List
            </button>
          </div>

          {/* FQC Form */}
          <div className="bg-white rounded-lg shadow">
            {template.categories.map((category, catIndex) => (
              <div key={catIndex} className="border-b last:border-b-0">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{category.category_name}</h3>
                  
                  {category.parameters.map((parameter, paramIndex) => (
                    <div key={paramIndex} className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {parameter.parameter_name}
                      </label>
                      <div className="space-y-2">
                        {parameter.options.map((option, optIndex) => {
                          const responseKey = `${category.category_id}_${parameter.parameter_id}`;
                          const isSelected = responses[responseKey] === option.option_id;
                          
                          return (
                            <div
                              key={optIndex}
                              onClick={() => handleResponseChange(category.category_id, parameter.parameter_id, option.option_id)}
                              className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{option.option_text}</span>
                                {isSelected && <CheckCircle className="w-5 h-5 text-purple-600" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Submit Buttons */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <button
              onClick={() => handleSubmitFQC('pass')}
              disabled={loading || Object.keys(responses).length === 0}
              className="py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-6 h-6" />
              {loading ? 'Submitting...' : 'FQC PASS'}
            </button>
            <button
              onClick={() => handleSubmitFQC('fail')}
              disabled={loading || Object.keys(responses).length === 0}
              className="py-4 bg-red-600 text-white rounded-lg font-bold text-lg hover:bg-red-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
            >
              <XCircle className="w-6 h-6" />
              {loading ? 'Submitting...' : 'FQC FAIL (RTR)'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Award className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Final Quality Check (FQC)</h1>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search devices by DID, brand, or model..."
                className="w-full pl-10 pr-4 py-3 border-2 rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Devices List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">
              Repair Completed Devices ({filteredDevices.length})
            </h2>
          </div>

          {filteredDevices.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Award className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No devices ready for FQC</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredDevices.map((device, index) => (
                <div
                  key={index}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-bold text-xl text-gray-900 mb-1">{device.did}</div>
                      <div className="text-gray-600">{device.brand} {device.model}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        TRC ID: {device.trc_device_id}
                      </div>
                      {device.repair_notes && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-900">
                          <strong>Repair Notes:</strong> {device.repair_notes}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleSelectDevice(device)}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700"
                    >
                      Start FQC
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FQCPage;
